/**
 * Group Result Card Component
 * Displays a group in search results
 */

import { useState } from 'react'
import { Card, CardContent, Box, Typography, Chip, Avatar, AvatarGroup, IconButton, Tooltip, Snackbar, Alert } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  People as PeopleIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
  ExitToApp as LeaveIcon,
  LocalOffer as TagIcon,
  HourglassEmpty as PendingIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
  Chat as ChatIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { GroupVisibility, getGroupVisibilityName } from '@api/types/social'
import type { GroupSearchResult } from '../../../api/types/search'

interface GroupResultCardProps {
  group: GroupSearchResult
}

export function GroupResultCard({ group }: GroupResultCardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isMember, setIsMember] = useState(group.isUserMember)
  const [hasPendingRequest, setHasPendingRequest] = useState(group.hasPendingJoinRequest ?? false)
  const [pendingRequestId, setPendingRequestId] = useState(group.pendingJoinRequestId ?? null)
  const [hasPendingInvite, setHasPendingInvite] = useState(group.hasPendingInvite ?? false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'info' | 'error' }>({
    open: false,
    message: '',
    severity: 'info'
  })

  const handleClick = () => {
    navigate(`/groups/${group.id}`)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getMemberRoleLabel = (role: number) => {
    const roles = ['Member', 'Moderator', 'Admin', 'Owner']
    return roles[role] || 'Member'
  }

  // Join group mutation (unified endpoint handles both direct join and join requests)
  const joinGroupMutation = useMutation({
    mutationFn: () => socialApi.joinGroup(group.id),
    onSuccess: (result) => {
      if (result.joinedDirectly) {
        setIsMember(true)
        queryClient.invalidateQueries({ queryKey: ['my-groups'] })
        setSnackbar({
          open: true,
          message: result.message || `Successfully joined ${group.name}`,
          severity: 'success'
        })
      } else if (result.joinRequestCreated) {
        setHasPendingRequest(true)
        setPendingRequestId(result.joinRequestId)
        setSnackbar({
          open: true,
          message: result.message || 'Join request submitted! Group moderators will review your request.',
          severity: 'info'
        })
      }
      queryClient.invalidateQueries({ queryKey: ['search'] })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to join group'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Cancel join request mutation
  const cancelRequestMutation = useMutation({
    mutationFn: () => socialApi.cancelJoinRequest(group.id, pendingRequestId!),
    onSuccess: () => {
      setHasPendingRequest(false)
      setPendingRequestId(null)
      queryClient.invalidateQueries({ queryKey: ['search'] })
      setSnackbar({
        open: true,
        message: 'Join request cancelled',
        severity: 'info'
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to cancel request'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Accept group invite mutation
  const acceptInviteMutation = useMutation({
    mutationFn: () => socialApi.acceptGroupInvite(group.id),
    onSuccess: () => {
      setIsMember(true)
      setHasPendingInvite(false)
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['search'] })
      setSnackbar({
        open: true,
        message: `Successfully joined ${group.name}`,
        severity: 'success'
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to accept invite'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Decline group invite mutation
  const declineInviteMutation = useMutation({
    mutationFn: () => socialApi.declineGroupInvite(group.id),
    onSuccess: () => {
      setHasPendingInvite(false)
      queryClient.invalidateQueries({ queryKey: ['search'] })
      setSnackbar({
        open: true,
        message: 'Invitation declined',
        severity: 'info'
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to decline invite'
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    },
  })

  // Leave group mutation
  const leaveGroupMutation = useMutation({
    mutationFn: () => socialApi.leaveGroup(group.id),
    onSuccess: () => {
      setIsMember(false)
      queryClient.invalidateQueries({ queryKey: ['my-groups'] })
      queryClient.invalidateQueries({ queryKey: ['search'] })
    },
  })

  const handleJoinLeave = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isMember) {
      if (confirm(`Are you sure you want to leave ${group.name}?`)) {
        leaveGroupMutation.mutate()
      }
    } else {
      joinGroupMutation.mutate()
    }
  }

  return (
    <Box sx={{ position: 'relative', mb: 2, mt: 3 }}>
      {/* Type Badge - Positioned above card */}
      <Chip
        label="Group"
        size="small"
        color="info"
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          zIndex: 1,
        }}
      />
      <Card
        sx={{
          mt: 0,
          transition: 'all 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          {/* Group Info Row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {/* Avatar */}
            <Avatar
              src={group.groupImage}
              alt={group.name}
              variant="rounded"
              sx={{ width: 56, height: 56, cursor: 'pointer', flexShrink: 0, borderRadius: 2 }}
              onClick={handleClick}
            >
              <PeopleIcon />
            </Avatar>

            {/* Group Details */}
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleClick}>
              {/* Group Name with Public/Private chip and Member Count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="h6" component="h3">
                  {group.name}
                </Typography>

                {/* Visibility Badge */}
                <Chip
                  icon={
                    (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.Public ? <PublicIcon /> :
                    (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.MatchingTagRequired ? <TagIcon /> :
                    <LockIcon />
                  }
                  label={getGroupVisibilityName(group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly))}
                  size="small"
                  color={
                    (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.Public ? 'success' :
                    (group.visibility ?? (group.isPublic ? GroupVisibility.Public : GroupVisibility.InviteOnly)) === GroupVisibility.MatchingTagRequired ? 'primary' :
                    'default'
                  }
                  variant="outlined"
                />

                {/* Member Count - Compact */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {group.memberCount}
                    {group.maxMembers > 0 && ` / ${group.maxMembers}`}
                  </Typography>
                </Box>

                {/* Chat Access Indicator */}
                {group.canAccessChat && (
                  <Tooltip title="Chat Available">
                    <ChatIcon fontSize="small" color="primary" />
                  </Tooltip>
                )}
              </Box>

              {/* Description */}
              {group.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {group.description}
                </Typography>
              )}

              {/* Status Row */}
              {group.isArchived && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Chip
                    label="Archived"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Box>
              )}

              {/* Interest Tags */}
              {group.interestTags && group.interestTags.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {group.interestTags.slice(0, 5).map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.displayName || tag.name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {group.interestTags.length > 5 && (
                    <Chip
                      label={`+${group.interestTags.length - 5} more`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}

              {/* Member Avatars */}
              {group.members && group.members.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <AvatarGroup max={5} sx={{ justifyContent: 'flex-start' }}>
                    {group.members.slice(0, 5).map((member) => (
                      <Avatar
                        key={member.userId}
                        src={member.userAvatar}
                        alt={member.displayName}
                        sx={{ width: 32, height: 32 }}
                      >
                        {member.displayName?.charAt(0).toUpperCase()}
                      </Avatar>
                    ))}
                  </AvatarGroup>
                </Box>
              )}
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0, alignItems: 'flex-end' }}>
              {/* Member Role Chip */}
              {group.isUserMember && (
                <Chip
                  label={group.isOwner ? 'Owner' : getMemberRoleLabel(group.userRole)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}

              {/* Already a member - show Leave button */}
              {isMember && (
                <Tooltip title="Leave Group">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={handleJoinLeave}
                    disabled={leaveGroupMutation.isPending}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    <LeaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Has pending join request - show Cancel button */}
              {!isMember && hasPendingRequest && (
                <Tooltip title="Cancel Request">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={(e) => {
                      e.stopPropagation()
                      cancelRequestMutation.mutate()
                    }}
                    disabled={cancelRequestMutation.isPending}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Has pending invite - show Accept/Decline buttons */}
              {!isMember && hasPendingInvite && (
                <>
                  <Tooltip title="Accept Invite">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation()
                        acceptInviteMutation.mutate()
                      }}
                      disabled={acceptInviteMutation.isPending || declineInviteMutation.isPending}
                      sx={{ bgcolor: 'action.hover' }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Decline Invite">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation()
                        declineInviteMutation.mutate()
                      }}
                      disabled={acceptInviteMutation.isPending || declineInviteMutation.isPending}
                      sx={{ bgcolor: 'action.hover' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              {/* No pending status - show Join button */}
              {!isMember && !hasPendingRequest && !hasPendingInvite && (
                <Tooltip title="Join Group">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={handleJoinLeave}
                    disabled={joinGroupMutation.isPending}
                    sx={{ bgcolor: 'action.hover' }}
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
    </Card>

      {/* Snackbar for join/leave feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
